import React, { useState } from 'react'

/**
 * USOKIE Platform Login Form
 * Clean, open design with generous spacing
 */
const LoginForm = ({ onSuccess }) => {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const response = await fetch('/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (response.ok) {
        if (onSuccess) {
          onSuccess(email)
        }
      } else if (response.status === 429) {
        setError(data.message || 'Too many login requests. Please try again later.')
      } else {
        setError(data.error || 'An error occurred. Please try again.')
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full">
      {/* Mobile logo - only visible on small screens */}
      <div className="lg:hidden mb-16">
        <img
          src="/images/usokie-logo-bone.png"
          alt="USOKIE"
          className="w-40 h-auto"
        />
      </div>

      {/* Header */}
      <div className="mb-16">
        <div className="flex items-center gap-4 mb-8">
          <span
            className="text-xs text-[#a1c6a2] tracking-[0.3em] uppercase"
            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
          >
            Authentication
          </span>
          <div className="flex-1 h-px bg-[rgba(255,251,247,0.1)]" />
        </div>

        <h1 className="text-4xl lg:text-5xl font-bold text-[#fffbf7] tracking-tight mb-6">
          Welcome Back
        </h1>
        <p className="text-lg text-[rgba(255,251,247,0.5)] leading-relaxed">
          Enter your email to receive a secure login link.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="mb-16">
        {/* Email Field */}
        <div className="mb-10">
          <label
            htmlFor="email"
            className="text-xs text-[rgba(255,251,247,0.4)] tracking-[0.2em] uppercase block mb-4"
            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
          >
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            disabled={loading}
            autoFocus
            className="w-full py-5 text-xl text-[#fffbf7] bg-transparent border-b-2 border-[rgba(255,251,247,0.2)] focus:border-[#a1c6a2] focus:outline-none transition-colors disabled:opacity-50 placeholder:text-[rgba(255,251,247,0.25)]"
            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
          />
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-10 py-5 border-l-2 border-[#c47474] pl-6">
            <p className="text-base text-[#c47474]">{error}</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading || !email}
          className="w-full bg-[#fffbf7] text-black px-8 py-5 text-sm font-semibold tracking-[0.15em] uppercase hover:bg-[#a1c6a2] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ fontFamily: "'GT Pressura Mono', monospace" }}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-3">
              <span className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              Sending...
            </span>
          ) : (
            'Send Login Link'
          )}
        </button>
      </form>

      {/* Footer info */}
      <div className="flex items-center gap-6 mb-16">
        <span
          className="text-xs text-[rgba(255,251,247,0.3)] tracking-wider"
          style={{ fontFamily: "'GT Pressura Mono', monospace" }}
        >
          Link expires in 15 minutes
        </span>
        <div className="w-1 h-1 bg-[rgba(255,251,247,0.2)] rounded-full" />
        <span
          className="text-xs text-[rgba(255,251,247,0.3)] tracking-wider"
          style={{ fontFamily: "'GT Pressura Mono', monospace" }}
        >
          No password required
        </span>
      </div>

      {/* Registration Link */}
      <div className="pt-8 border-t border-[rgba(255,251,247,0.08)]">
        <span className="text-base text-[rgba(255,251,247,0.4)]">
          Don't have an account?{' '}
        </span>
        <a
          href="/registrations/new"
          data-turbo="false"
          className="text-base text-[#a1c6a2] hover:text-[#fffbf7] transition-colors"
          style={{ fontFamily: "'GT Pressura Mono', monospace" }}
        >
          Request Access
        </a>
      </div>
    </div>
  )
}

export default LoginForm
